import { useState, type FormEvent } from "react";

import { Alert, Input } from "@sirdab/ui";

import { useApi, type ApiFailure } from "../../api";
import { ErrorState, Field, StepActions } from "../../components";
import { useFlow } from "../../flow";
import { isPlausibleSaudiMobile, toWirePhone } from "../../format/phone";
import { useMessages, usePortalCopy } from "../../i18n";
import { OfflineNote, useFailureText } from "../shared";
import { StepScaffold } from "../StepScaffold";

/**
 * The phone alone. Nothing here says whether the number owns any orders: the server
 * answers a known and an unknown phone identically, and so does this screen.
 *
 * It is also the only way in, which is why a dead deep link says so HERE: the order
 * number form needs the session this step starts, so there is nowhere earlier to say it.
 */
export function PhoneStep({
  initialPhone,
  linkFailed = false,
  onSent,
}: {
  initialPhone: string;
  /** Set when a minted link failed to resolve, so this step explains the detour. */
  linkFailed?: boolean | undefined;
  /** The code went out to this canonical phone. */
  onSent: (wirePhone: string) => void;
}): React.JSX.Element {
  const api = useApi();
  const { t, locale } = useMessages();
  const c = usePortalCopy();
  const { merchantSlug } = useFlow();
  const failureText = useFailureText();

  const [phoneValue, setPhoneValue] = useState(initialPhone);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const phoneError = !touched
    ? undefined
    : phoneValue.trim() === ""
      ? t("validation.phone_required")
      : !isPlausibleSaudiMobile(phoneValue)
        ? c("lookup.phone_invalid")
        : undefined;

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setTouched(true);
    if (phoneValue.trim() === "") return;

    const wirePhone = toWirePhone(phoneValue);
    setFailure(null);
    setBusy(true);
    // The SMS goes out in the language the shopper is reading the page in.
    const result = await api.requestShopperCode({
      merchantSlug,
      phone: wirePhone,
      locale,
    });
    setBusy(false);
    if (!result.ok) {
      setFailure(result);
      return;
    }
    onSent(wirePhone);
  }

  return (
    <StepScaffold
      step="lookup"
      titleKey="screen.lookup.title"
      descriptionKey="screen.lookup.phone_description"
    >
      <form
        noValidate
        onSubmit={(event) => void submit(event)}
        className="flex flex-col gap-4"
      >
        {linkFailed ? (
          <Alert variant="warning">{c("link.failed")}</Alert>
        ) : null}

        <OfflineNote />

        <Field
          label={t("field.phone.label")}
          hint={`${t("field.phone.hint")} ${c("lookup.phone_formats")}`}
          error={phoneError}
          required
        >
          {(props) => (
            <Input
              {...props}
              size="touch"
              dir="ltr"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              autoFocus
              value={phoneValue}
              placeholder={t("field.phone.placeholder")}
              onChange={(event) => setPhoneValue(event.target.value)}
            />
          )}
        </Field>

        {failure !== null ? (
          <ErrorState message={failureText(failure)} />
        ) : null}

        <StepActions
          nextType="submit"
          nextLabel={t("action.send_code")}
          nextLoading={busy}
        />
      </form>
    </StepScaffold>
  );
}
